"use client";
import {
  PlainDate,
  type PlainDateString,
  formatPlainDate,
  toPlainDateString,
} from "@sa-nu/shared-utils/date";

import type { MonthGridCell } from "../_utils/build-month-grids";
import { deriveDateCellState } from "../_utils/derive-date-cell-state";
import {
  type DatePickerSelection,
  getNextSelection,
} from "../_utils/get-next-selection";
import { isHolidayDate } from "../_utils/is-holiday-date";
import {
  type UnavailablePeriod,
  isDisabledByUnavailablePeriods,
} from "../_utils/unavailable-periods";
import { DateCell } from "../date-cell";
import type { DatePickerDateInfo } from "../date-picker";

/**
 * 1 ヶ月分のセルグリッド (日曜始まり・7 列)。
 * blank/DateCell の描画、baseDisabled 判定、deriveDateCellState、getNextSelection を担う。
 * `selection` は正規化済みを受け取る。
 */
export function MonthCells({
  cells,
  selection,
  today,
  minDate,
  maxDate,
  maxNights,
  dateInfoMap,
  unavailablePeriods = [],
  showPriceRow,
  onChange,
  tooltipTargetDate,
  tooltipMessage,
}: {
  cells: MonthGridCell[];
  selection: DatePickerSelection;
  today: PlainDate;
  minDate: PlainDate;
  maxDate: PlainDate;
  maxNights?: number;
  dateInfoMap?: Record<PlainDateString, DatePickerDateInfo>;
  unavailablePeriods?: readonly UnavailablePeriod[];
  showPriceRow: boolean;
  onChange: (next: DatePickerSelection) => void;
  /** ツールチップを表示する対象日。非表示のときは null */
  tooltipTargetDate: PlainDate | null;
  tooltipMessage: string;
}) {
  // CO 選択中 (単一選択モードを除く) だけ、期間をまたぐ CO を弾くために CI を渡す
  const checkInDate =
    maxNights !== undefined && selection.endDate === null
      ? selection.startDate
      : null;

  return (
    <div className="grid grid-cols-7 gap-y-[8px]">
      {cells.map((cell, index) => {
        if (cell.type === "blank") {
          return (
            <div key={`blank-${index}`} className="min-h-[40px] min-w-[40px]" />
          );
        }

        const { date } = cell;
        const dateStr = toPlainDateString(date);
        const info = dateInfoMap?.[dateStr];
        const baseDisabled =
          info?.disabled === true ||
          PlainDate.compare(date, minDate) < 0 ||
          PlainDate.compare(date, maxDate) > 0 ||
          isDisabledByUnavailablePeriods({
            date,
            unavailablePeriods,
            checkInDate,
          });

        const isHoliday = isHolidayDate(date);
        const { variant, continuity, isInteractive } = deriveDateCellState({
          date,
          selection,
          today,
          baseDisabled,
          disabledAppearance: info?.disabledAppearance,
          maxNights,
          isHoliday,
        });

        const isSelected = variant === "selected" || variant === "inRange";

        const ariaLabel = formatPlainDate(date, {
          format: "ja",
          withDayOfWeek: false,
        });

        const showTooltip =
          tooltipTargetDate !== null &&
          PlainDate.compare(date, tooltipTargetDate) === 0;

        return (
          <DateCell
            key={date.toString()}
            date={dateStr}
            day={date.day}
            variant={variant}
            continuity={continuity}
            isHoliday={isHoliday}
            price={info?.price}
            showPriceRow={showPriceRow}
            isInteractive={isInteractive}
            ariaLabel={ariaLabel}
            isSelected={isSelected}
            tooltipMessage={showTooltip ? tooltipMessage : undefined}
            onClick={() => {
              onChange(
                getNextSelection({
                  current: selection,
                  clicked: date,
                  clickedDisabled: baseDisabled,
                  maxNights,
                }),
              );
            }}
          />
        );
      })}
    </div>
  );
}
